import React, { useState } from 'react';
import { Col, Label } from 'reactstrap';
import ActionButton from '../../../Components/Common/ActionButton';
import { confirmRemove } from '../../../utils/confirm';
import { toggleProps } from '../../../utils/a11y';
import { formatDateToddmmyyhhmm } from '../../../utils/dateformatter';
import {
  getOwnClauseResponse,
  hasClauseResponse,
  isBidderClauseConfirmed,
  isEvaluatorResponse,
  needsIcReplyOnEvaluatorThread,
  getLastOwnEditableResponse,
  canAgreeLastCounter,
  toClauseResponseList,
  sortRespondClauseDetails,
  toLockedFlag,
} from '../respond';
import AgreeLockButton from './AgreeLockButton';
import { RespondNotesEditor, RespondMessageBubble } from '../ui';
import { COUNTER_ACTION_BTN_STYLE } from '../constants';

const MandatoryClauseBadge = () => (
  <span className='badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill fw-medium fs-11'>
    Mandatory
  </span>
);

const ConfirmedClauseBadge = () => (
  <span className='badge bg-success-subtle text-success border border-success-subtle rounded-pill fw-medium fs-11'>
    Confirmed
  </span>
);

const ClauseHtmlContent = ({ value }) => {
  if (value == null || typeof value === 'object') {
    return <div className='text-dark mb-0 lh-base fs-13'>-</div>;
  }
  const text = String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  if (!text) {
    return <div className='text-dark mb-0 lh-base fs-13'>-</div>;
  }
  return (
    <div
      className='text-dark mb-0 lh-base fs-13'
      dangerouslySetInnerHTML={{ __html: String(value) }}
    />
  );
};

const hasClauseDescription = value => {
  if (value == null || typeof value === 'object') return false;
  const text = String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return text.length > 0;
};

const formatResponseMetaDate = response => {
  const value = response?.updatedDate || response?.createdDate;
  if (!value) return null;
  return formatDateToddmmyyhhmm(value) || null;
};

const ClauseCard = ({
  clause,
  index,
  clauseResponses,
  draftNotes,
  expandedClauses,
  formMain: _formMain,
  isClauseResponsesSaving,
  isClauseResponsesLoading,
  isEvaluatorView = true,
  handleDraftChange,
  handleClauseSecondaryAction,
  handleClauseSave,
  toggleClauseExpand,
  setErrors,
  onLockResponse,
  canInitiate = false,
  disableRespond = false,
  isRespondClosed = false,
  highlightPending = false,
  anchorId,
  // String = free-text item: Agree & Lock asks for a Final value (prefill).
  finalValuePrefill,
  // Offer-stage Confirm only applies to GTC clauses, not per-field threads.
  showBidderConfirmation = true,
}) => {
  const responseList = sortRespondClauseDetails(
    toClauseResponseList(clauseResponses[clause.id])
  );
  const ownResponse = getOwnClauseResponse(responseList, {
    isEvaluator: isEvaluatorView,
  });
  const messageCount = responseList.filter(item =>
    hasClauseResponse(item?.respondNotes)
  ).length;
  const hasAnySaved = messageCount > 0;
  const hasCounterResponse = responseList.some(
    item => hasClauseResponse(item?.respondNotes) && !isEvaluatorResponse(item)
  );
  const lastNeedsIcReply = needsIcReplyOnEvaluatorThread(responseList);
  const lastOwnEditable = getLastOwnEditableResponse(responseList, {
    isEvaluator: isEvaluatorView,
  });
  const lastMessage = responseList.filter(item =>
    hasClauseResponse(item?.respondNotes)
  );
  const isThreadLocked = toLockedFlag(
    lastMessage[lastMessage.length - 1]?.isLocked
  );

  const isExpanded =
    expandedClauses[clause.id] !== undefined
      ? Boolean(expandedClauses[clause.id])
      : hasAnySaved;

  const savedNotes = lastOwnEditable?.respondNotes ?? '';
  const hasOwnSaved = hasClauseResponse(savedNotes);
  const draftValue = draftNotes[clause.id] ?? (hasOwnSaved ? savedNotes : '');
  const isDraftDirty = String(draftValue ?? '') !== String(savedNotes ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const canAddRespond =
    isRespondClosed || disableRespond || isThreadLocked
      ? false
      : isEvaluatorView
        ? (hasCounterResponse || canInitiate) && !hasOwnSaved
        : lastNeedsIcReply;
  const showRespondButton = canAddRespond && !isEditing;
  const showRespondEditor =
    !isRespondClosed && !disableRespond && !isThreadLocked && isEditing;
  const isMandatory = clause.isShowToDMUT === true;
  const bidderConfirmed =
    showBidderConfirmation && isBidderClauseConfirmed(responseList);

  return (
    <Col sm={12} className='mb-2' id={anchorId}>
      <div
        className={`rounded-3 overflow-hidden bg-white ${
          highlightPending
            ? 'border border-danger border-2 shadow-sm'
            : isExpanded
              ? 'border border-primary shadow-sm'
              : 'border'
        }`}
        style={highlightPending ? { scrollMarginTop: 96 } : undefined}
      >
        <div className='px-3 py-3 d-flex align-items-center gap-3 border-bottom bg-light-subtle'>
          {index != null && (
            <span
              className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold fs-12 ${
                isExpanded
                  ? 'bg-primary text-white'
                  : 'bg-light text-muted border'
              }`}
              style={{ width: '28px', height: '28px' }}
            >
              {index}
            </span>
          )}
          <div className='flex-grow-1 min-w-0'>
            <div className='d-flex align-items-center gap-2 flex-wrap'>
              <span
                className={`fs-14 mb-0 ${
                  isExpanded ? 'fw-bold text-dark' : 'fw-semibold text-dark'
                }`}
              >
                {clause.clauseTitle}
              </span>
              {isMandatory && <MandatoryClauseBadge />}
              {bidderConfirmed && <ConfirmedClauseBadge />}
            </div>
          </div>
        </div>

        <div className='p-3'>
          {isMandatory && (
            <div className='text-danger small fw-medium mb-2'>
              * Mandatory clause for DMUT to fulfill
            </div>
          )}
          {hasClauseDescription(clause.clauseDescription) && (
            <div className='p-3 rounded-3 bg-light-subtle border text-muted fs-13 lh-base'>
              <ClauseHtmlContent value={clause.clauseDescription} />
            </div>
          )}

          <div
            className={`border-top pt-3 d-flex justify-content-between align-items-center${
              hasClauseDescription(clause.clauseDescription) ? ' mt-3' : ''
            }`}
          >
            <div
              className='d-flex align-items-center gap-2'
              style={{ cursor: 'pointer' }}
              {...toggleProps(
                () => toggleClauseExpand(clause.id),
                isExpanded,
                undefined,
                'expanded'
              )}
            >
              <span
                className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                  isExpanded
                    ? 'bg-primary-subtle text-primary'
                    : 'bg-light text-muted'
                }`}
                style={{ width: '28px', height: '28px' }}
              >
                <i
                  className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
                  aria-hidden='true'
                ></i>
              </span>
              {hasAnySaved ? (
                <>
                  <span
                    className='bg-danger text-white rounded-pill d-inline-flex align-items-center justify-content-center fw-semibold fs-11'
                    style={{
                      minWidth: '20px',
                      height: '20px',
                      padding: messageCount > 9 ? '0 6px' : 0,
                    }}
                  >
                    {messageCount}
                  </span>
                  <span className='fw-semibold text-dark fs-13'>Response</span>
                </>
              ) : bidderConfirmed ? (
                <>
                  <i
                    className='ri-checkbox-circle-fill text-success fs-16'
                    aria-hidden='true'
                  ></i>
                  <span className='fw-semibold text-success fs-13'>
                    Confirmed
                  </span>
                </>
              ) : (
                <span className='text-muted fs-13'>No responses yet</span>
              )}
            </div>

            {showRespondButton && (
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-chat-3-line'
                style={COUNTER_ACTION_BTN_STYLE}
                onClick={() => {
                  if (!isExpanded) toggleClauseExpand(clause.id);
                  handleDraftChange(clause.id, '');
                  setIsEditing(true);
                }}
                disabled={isClauseResponsesLoading}
              >
                Respond
              </ActionButton>
            )}
          </div>

          {isExpanded && (hasAnySaved || isEditing) && (
            <div className='mt-3'>
              {responseList.map(response => {
                const notes = response?.respondNotes ?? '';
                if (!hasClauseResponse(notes)) return null;

                const evaluatorItem = isEvaluatorResponse(response);
                const canManage =
                  !isRespondClosed &&
                  lastOwnEditable &&
                  String(response.id) === String(lastOwnEditable.id);
                const isOwnEditing =
                  canManage &&
                  isEditing &&
                  String(ownResponse?.id ?? lastOwnEditable?.id ?? '') ===
                    String(response?.id ?? '');
                const metaDate = formatResponseMetaDate(response);
                const authorLabel = evaluatorItem
                  ? response.createdByLabel || 'PT Pertamina Patra Niaga'
                  : response.createdByLabel || 'Counter';

                if (isOwnEditing) return null;

                return (
                  <RespondMessageBubble
                    key={response.id ?? `${clause.id}-${metaDate}-${notes}`}
                    isEvaluator={evaluatorItem}
                    content={notes}
                    authorLabel={authorLabel}
                    metaDate={metaDate}
                    footer={
                      <>
                        {canAgreeLastCounter(responseList, response) &&
                          (!isRespondClosed || response.isLocked) && (
                            <div className='mt-3 pt-2 border-top'>
                              <AgreeLockButton
                                embedded
                                isLocked={response.isLocked}
                                finalValue={response.finalValue}
                                finalValuePrefill={finalValuePrefill}
                                onLock={
                                  !isRespondClosed &&
                                  isEvaluatorView &&
                                  response.id &&
                                  onLockResponse
                                    ? (locked, finalValue) =>
                                        onLockResponse(
                                          response.id,
                                          locked,
                                          finalValue
                                        )
                                    : undefined
                                }
                                disabled={isClauseResponsesSaving}
                              />
                            </div>
                          )}
                        {canManage &&
                          !response.isLocked &&
                          !isRespondClosed && (
                            <div className='mt-3 pt-2 border-top d-flex align-items-center gap-2'>
                              <ActionButton
                                variant='secondary'
                                size='sm'
                                className='flex-grow-1'
                                onClick={() => {
                                  handleDraftChange(
                                    clause.id,
                                    lastOwnEditable?.respondNotes ?? ''
                                  );
                                  setIsEditing(true);
                                }}
                              >
                                Edit
                              </ActionButton>
                              <ActionButton
                                variant='destructive'
                                size='sm'
                                confirm={false}
                                className='flex-grow-1'
                                onClick={async () => {
                                  const confirmed = await confirmRemove(
                                    'response note',
                                    clause.clauseTitle,
                                    {
                                      message:
                                        'Your response note will be permanently deleted. This cannot be undone.',
                                    }
                                  );
                                  if (!confirmed) return undefined;
                                  const deleting = handleClauseSecondaryAction(
                                    clause.id,
                                    true
                                  );
                                  setIsEditing(false);
                                  return deleting;
                                }}
                              >
                                Remove
                              </ActionButton>
                            </div>
                          )}
                      </>
                    }
                  />
                );
              })}

              {showRespondEditor && (
                <div className='mt-2'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <Label className='form-label fw-semibold text-dark fs-13 mb-0'>
                      Respond Notes
                    </Label>
                    {isDraftDirty && (
                      <span className='text-warning small fs-12 fw-medium d-flex align-items-center gap-1'>
                        <i className='ri-error-warning-line fs-14'></i>
                        Unsaved Changes
                      </span>
                    )}
                  </div>

                  <RespondNotesEditor
                    id={clause.id}
                    value={draftValue}
                    onChange={e => {
                      handleDraftChange(clause.id, e.target.value);
                      if (typeof setErrors === 'function') {
                        setErrors(prev => ({ ...prev, [clause.id]: '' }));
                      }
                    }}
                    isDraftDirty={isDraftDirty}
                  />

                  <div className='d-flex gap-2 mt-3 justify-content-end'>
                    <ActionButton
                      variant='secondary'
                      onClick={() => {
                        handleDraftChange(
                          clause.id,
                          hasOwnSaved
                            ? (lastOwnEditable?.respondNotes ?? '')
                            : ''
                        );
                        setIsEditing(false);
                        if (!hasOwnSaved && !hasAnySaved) {
                          toggleClauseExpand(clause.id);
                        }
                      }}
                      disabled={isClauseResponsesSaving}
                    >
                      Cancel
                    </ActionButton>

                    <ActionButton
                      variant='primary'
                      onClick={async () => {
                        await handleClauseSave(clause.id);
                        setIsEditing(false);
                      }}
                      loading={isClauseResponsesSaving}
                      disabled={isClauseResponsesSaving || !isDraftDirty}
                    >
                      {isClauseResponsesSaving ? 'Saving...' : 'Send Response'}
                    </ActionButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Col>
  );
};

export default ClauseCard;
